import { DebouncedAccessor, Setter } from '@/lib/common-utils';
import { usePagination } from '@/lib/pagination';
import { FullSearchSchemaOfRouteId, RegisteredRouteTree, useDebouncedSearchState } from '@/lib/routing';
import { type AnyRoute, type RouteIds } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useState } from 'react';

/**
 * Hook to manage paginated filters with automatic page reset on filter change.
 * Returns individual filter state/accessors per key and exposes pagination state.
 */

export function usePaginatedFilters<
  TId extends RouteIds<TRouteTree>,
  TRouteTree extends AnyRoute = RegisteredRouteTree,
  TFullSearchSchema extends object = FullSearchSchemaOfRouteId<TId, TRouteTree>,
  QueryStringKey extends keyof TFullSearchSchema = keyof TFullSearchSchema,
  QueryStringKeys extends readonly [QueryStringKey, ...QueryStringKey[]] = readonly [
    QueryStringKey,
    ...QueryStringKey[],
  ],
>(routeId: TId, keys: QueryStringKeys, countActiveOf?: QueryStringKeys) {
  const { pageIndex, pageSize, onPaginationChange, resetPagination } = usePagination(routeId);

  const wrapSetterWithReset = useCallback(
    <T>(setValue: Setter<T>): Setter<T> =>
      (value: T) => {
        setValue(value);
        resetPagination();
      },
    [resetPagination],
  );

  // We assume `keys` is always the same for every render of this hook.
  // Using `readonly` helps document that we don't intend to mutate it, but it doesn't enforce immutability from the caller's side.
  function buildFiltersMap(keys: QueryStringKeys): {
    [K in QueryStringKeys[number]]: DebouncedAccessor<TFullSearchSchema[K] | undefined>;
  } {
    return keys.reduce((acc, key) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      const [values, setValue] = useDebouncedSearchState(routeId, key);
      acc[key] = [values, wrapSetterWithReset(setValue)];
      return acc;
    }, Object.create(null));
  }

  const filters = buildFiltersMap(keys);

  const resetFilters = useCallback(() => {
    keys.forEach((key) => {
      const [, setValue] = filters[key];
      setValue(undefined);
    });
    resetPagination();
  }, [filters, keys, resetPagination]);

  const activeFiltersCount = useMemo(() => {
    if (!countActiveOf?.length) return 0;

    const isActive = (value: unknown) =>
      Array.isArray(value) ? value.length > 0 : value !== undefined && value !== null && value !== '';

    return countActiveOf.reduce((acc, key) => {
      const [[liveValue]] = filters[key] as DebouncedAccessor<unknown>;
      return acc + (isActive(liveValue) ? 1 : 0);
    }, 0);
  }, [filters, countActiveOf]);

  return {
    pageIndex,
    pageSize,
    onPaginationChange,
    resetPagination,
    filters,
    resetFilters,
    activeFiltersCount,
  };
}

/**
 * Hook to manage filters for infinite scroll tables (without pagination).
 * Returns individual filter state/accessors per key with debouncing.
 * Uses local state (not URL params) so it works in any component context.
 *
 * @example
 * ```tsx
 * const { filters, resetFilters } = useInfiniteScrollFilters(['query']);
 * // filters.query is [[liveValue, debouncedValue], setter]
 * // Use debouncedValue[1] (second element) in API calls, debouncedValue[0] (first element) for UI
 * const [[queryLive, queryDebounced], setQuery] = filters.query;
 * ```
 */
export function useInfiniteScrollFilters<FilterKeys extends readonly [string, ...string[]]>(
  keys: FilterKeys,
  debounceDelay: number = 300,
): {
  filters: {
    [K in FilterKeys[number]]: DebouncedAccessor<string | undefined>;
  };
  resetFilters: () => void;
  activeFiltersCount: number;
} {
  // We need to call hooks unconditionally, so we'll create a map
  // For a small, fixed number of keys, this is acceptable
  // Note: This assumes keys array is stable across renders

  // Create state for each key - we'll use a pattern that works with hooks rules
  // Since we can't dynamically call hooks, we'll use a different approach:
  // Create a single state object that holds all filter values
  const [filterValues, setFilterValues] = useState<Record<string, string | undefined>>(() => {
    return keys.reduce(
      (acc, key) => {
        acc[key] = undefined;
        return acc;
      },
      {} as Record<string, string | undefined>,
    );
  });

  // Create debounced values
  const [debouncedFilterValues, setDebouncedFilterValues] = useState<Record<string, string | undefined>>(() => {
    return keys.reduce(
      (acc, key) => {
        acc[key] = undefined;
        return acc;
      },
      {} as Record<string, string | undefined>,
    );
  });

  // Debounce each filter value
  useEffect(() => {
    const timeouts: Record<string, NodeJS.Timeout> = {};
    keys.forEach((key) => {
      timeouts[key] = setTimeout(() => {
        setDebouncedFilterValues((prev) => ({ ...prev, [key]: filterValues[key] }));
      }, debounceDelay);
    });
    return () => {
      keys.forEach((key) => {
        if (timeouts[key]) clearTimeout(timeouts[key]);
      });
    };
  }, [keys, filterValues, debounceDelay]);

  // Build filters object with DebouncedAccessor format
  const filters = useMemo(() => {
    const result = {} as {
      [K in FilterKeys[number]]: DebouncedAccessor<string | undefined>;
    };
    keys.forEach((key) => {
      result[key as FilterKeys[number]] = [
        [filterValues[key], debouncedFilterValues[key]],
        (value: string | undefined) => {
          setFilterValues((prev) => ({ ...prev, [key]: value }));
        },
      ] as DebouncedAccessor<string | undefined>;
    });
    return result;
  }, [keys, filterValues, debouncedFilterValues]);

  const resetFilters = useCallback(() => {
    setFilterValues((prev) => {
      const reset = { ...prev };
      keys.forEach((key) => {
        reset[key] = undefined;
      });
      return reset;
    });
  }, [keys]);

  const activeFiltersCount = useMemo(() => {
    const isActive = (value: unknown) => value !== undefined && value !== null && value !== '';

    let count = 0;
    keys.forEach((key) => {
      const filterKey = key as FilterKeys[number];
      const [[liveValue]] = filters[filterKey];
      if (isActive(liveValue)) count++;
    });
    return count;
  }, [filters, keys]);

  return {
    filters,
    resetFilters,
    activeFiltersCount,
  };
}

// Helpers for boolean filters
export function booleanFilterToSelectedValues(value: boolean | undefined): string[] {
  return value !== undefined ? [String(value)] : [];
}

export function selectedValuesToBooleanFilter(values: string[]): boolean | undefined {
  return values.length === 0 ? undefined : values[0] === 'true';
}
